"use client";

import type React from "react";
import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Heading,
  Spinner,
  Center,
  Text,
  Badge,
  VStack,
  useColorModeValue,
  Collapse,
  Grid,
  GridItem,
  useToast,
  IconButton,
  HStack,
} from "@chakra-ui/react";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CopyIcon,
  RepeatIcon,
} from "@chakra-ui/icons";
import { createClient, type SupabaseClient } from "@supabase/supabase-js";

interface UserData {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  status: string;
  created_at: string;
  subscription_status?: string;
  stripe_subscription_id?: string;
  total_credits?: number;
  used_credits?: number;
  available_credits?: number;
}

interface TenantProfile {
  id: string;
  created_at?: string;
}

interface TenantSubscription {
  user_id: string;
  status?: string;
  stripe_subscription_id?: string;
}

interface TenantCreditBalance {
  user_id: string;
  total_credits?: number;
  used_credits?: number;
  available_credits?: number;
}

async function fetchAllAuthUsers(
  client: SupabaseClient,
): Promise<
  Array<{
    id: string;
    email?: string;
    created_at?: string;
    email_confirmed_at?: string | null;
    user_metadata?: Record<string, unknown>;
  }>
> {
  const allUsers: Array<{
    id: string;
    email?: string;
    created_at?: string;
    email_confirmed_at?: string | null;
    user_metadata?: Record<string, unknown>;
  }> = [];
  let page = 1;
  const perPage = 1000;

  while (true) {
    const { data, error } = await client.auth.admin.listUsers({ page, perPage });
    if (error) throw error;

    const batch = data?.users ?? [];
    allUsers.push(...batch);

    if (batch.length < perPage) break;
    page += 1;
  }

  return allUsers;
}

function metadataString(
  metadata: Record<string, unknown> | undefined,
  key: string,
): string {
  const value = metadata?.[key];
  return typeof value === "string" ? value : "";
}

interface UsersTableProps {
  supabaseUrl: string;
  supabaseServiceKey: string;
  onboardingData?: {
    company_name: string;
    main_logo_url: string;
    tab_logo_url: string;
    main_app_url: string;
    retellai_api_key: string;
    stripe_secret_key: string;
    monthly_subscription_price_usd?: number | null;
    one_credit_price_usd?: number | null;
    one_phone_number_monthly_price_usd?: number | null;
    ai_agents_call_price_per_minute_usd?: number | null;
  };
}

const UsersTable: React.FC<UsersTableProps> = ({
  supabaseUrl,
  supabaseServiceKey,
  onboardingData,
}) => {
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);

  const borderColor = useColorModeValue("gray.200", "gray.700");
  const bgColor = useColorModeValue("white", "gray.800");
  const collapseBgColor = useColorModeValue("gray.50", "gray.700");
  const toast = useToast();

  const fetchUsers = useCallback(async () => {
    if (!supabaseUrl || !supabaseServiceKey) {
      setError("Supabase credentials are not available yet");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const client = createClient(supabaseUrl, supabaseServiceKey);

      const [
        { data: profiles, error: profilesError },
        { data: subscriptions, error: subscriptionsError },
        { data: credits, error: creditsError },
        authUsers,
      ] = await Promise.all([
        client.from("profiles").select("id, created_at"),
        client
          .from("subscriptions")
          .select("user_id, status, stripe_subscription_id"),
        client
          .from("credit_balances")
          .select("user_id, total_credits, used_credits, available_credits"),
        fetchAllAuthUsers(client),
      ]);

      if (profilesError) throw profilesError;
      if (subscriptionsError) throw subscriptionsError;
      if (creditsError) throw creditsError;

      const profilesById = new Map(
        (profiles as TenantProfile[] | null)?.map((profile) => [
          profile.id,
          profile,
        ]) ?? [],
      );
      const subscriptionsByUserId = new Map(
        (subscriptions as TenantSubscription[] | null)?.map((subscription) => [
          subscription.user_id,
          subscription,
        ]) ?? [],
      );
      const creditsByUserId = new Map(
        (credits as TenantCreditBalance[] | null)?.map((credit) => [
          credit.user_id,
          credit,
        ]) ?? [],
      );

      const userIds = new Set<string>([
        ...authUsers.map((user) => user.id),
        ...(profiles ?? []).map((profile) => profile.id),
        ...(subscriptions ?? []).map((subscription) => subscription.user_id),
        ...(credits ?? []).map((credit) => credit.user_id),
      ]);

      const combinedUsers: UserData[] = Array.from(userIds).map((userId) => {
        const authUser = authUsers.find((user) => user.id === userId);
        const profile = profilesById.get(userId);
        const subscription = subscriptionsByUserId.get(userId);
        const creditBalance = creditsByUserId.get(userId);
        const metadata = authUser?.user_metadata ?? {};

        const totalCredits = creditBalance?.total_credits ?? 0;
        const usedCredits = creditBalance?.used_credits ?? 0;
        const availableCredits =
          creditBalance?.available_credits ?? totalCredits - usedCredits;

        return {
          id: userId,
          email: authUser?.email || "",
          first_name: metadataString(metadata, "first_name"),
          last_name: metadataString(metadata, "last_name"),
          role: metadataString(metadata, "role") || "user",
          status: authUser?.email_confirmed_at ? "active" : "pending",
          created_at: profile?.created_at || authUser?.created_at || "",
          subscription_status: subscription?.status || "none",
          stripe_subscription_id: subscription?.stripe_subscription_id || "",
          total_credits: totalCredits,
          used_credits: usedCredits,
          available_credits: availableCredits,
        };
      });

      combinedUsers.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );

      setUsers(combinedUsers);
      setError(null);
    } catch (err: any) {
      setError(err.message || "Failed to fetch users");
    } finally {
      setLoading(false);
    }
  }, [supabaseUrl, supabaseServiceKey]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleReload = async () => {
    toast({
      title: "Refreshing users...",
      status: "info",
      duration: 2000,
      isClosable: true,
      position: "top",
    });
    await fetchUsers();
  };

  const getSubscriptionColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "active":
        return "green";
      case "trialing":
        return "blue";
      case "canceled":
      case "unpaid":
        return "red";
      case "past_due":
        return "orange";
      case "none":
        return "gray";
      default:
        return "gray";
    }
  };

  const copyToClipboard = (value: string, label: string) => {
    navigator.clipboard.writeText(value).then(
      () => {
        toast({
          title: "Copied to clipboard",
          description: `${label} has been copied`,
          status: "success",
          duration: 2000,
          isClosable: true,
          position: "top",
        });
      },
      (err) => {
        toast({
          title: "Failed to copy",
          description: "Could not copy to clipboard",
          status: "error",
          duration: 2000,
          isClosable: true,
          position: "top",
        });
      }
    );
  };

  if (loading) {
    return (
      <Center py={10}>
        <VStack spacing={4}>
          <Spinner size="xl" color="primary.500" />
          <Text>Loading users...</Text>
        </VStack>
      </Center>
    );
  }

  if (error) {
    return (
      <Center py={10}>
        <VStack spacing={4}>
          <Text color="red.500" fontSize="lg">
            Error: {error}
          </Text>
        </VStack>
      </Center>
    );
  }

  return (
    <VStack spacing={4} w="100%" align="stretch">
      {onboardingData && (
        <Box
          w="100%"
          bg={bgColor}
          borderRadius="lg"
          border="1px solid"
          borderColor={borderColor}
          overflow="hidden"
        >
          <Box
            p={4}
            cursor="pointer"
            onClick={() => setIsOnboardingOpen(!isOnboardingOpen)}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Heading size="md">Onboarding Journey Data</Heading>
              {isOnboardingOpen ? (
                <ChevronUpIcon boxSize={6} />
              ) : (
                <ChevronDownIcon boxSize={6} />
              )}
            </Box>
            <Text fontSize="sm" color="gray.600" mt={1}>
              Click to {isOnboardingOpen ? "hide" : "view"} the information you
              submitted
            </Text>
          </Box>

          <Collapse in={isOnboardingOpen} animateOpacity>
            <Box p={6} pt={2} bg={collapseBgColor}>
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <GridItem>
                  <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={1}>
                    Company Name
                  </Text>
                  <Text>{onboardingData.company_name || "-"}</Text>
                </GridItem>

                <GridItem>
                  <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={1}>
                    Application URL
                  </Text>
                  <Text wordBreak="break-all">
                    {onboardingData.main_app_url || "-"}
                  </Text>
                </GridItem>

                <GridItem>
                  <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={1}>
                    RetellAI API Key
                  </Text>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={2}
                    cursor="pointer"
                    onClick={() =>
                      copyToClipboard(
                        onboardingData.retellai_api_key || "",
                        "RetellAI API Key"
                      )
                    }
                    _hover={{ bg: "gray.100" }}
                    p={2}
                    borderRadius="md"
                    transition="all 0.2s"
                  >
                    <Text fontFamily="mono" fontSize="sm" flex={1}>
                      {onboardingData.retellai_api_key
                        ? `${onboardingData.retellai_api_key.slice(0, 10)}...`
                        : "-"}
                    </Text>
                    <CopyIcon color="gray.500" />
                  </Box>
                </GridItem>

                <GridItem>
                  <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={1}>
                    Stripe Secret Key
                  </Text>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={2}
                    cursor="pointer"
                    onClick={() =>
                      copyToClipboard(
                        onboardingData.stripe_secret_key || "",
                        "Stripe Secret Key"
                      )
                    }
                    _hover={{ bg: "gray.100" }}
                    p={2}
                    borderRadius="md"
                    transition="all 0.2s"
                  >
                    <Text fontFamily="mono" fontSize="sm" flex={1}>
                      {onboardingData.stripe_secret_key
                        ? `${onboardingData.stripe_secret_key.slice(0, 10)}...`
                        : "-"}
                    </Text>
                    <CopyIcon color="gray.500" />
                  </Box>
                </GridItem>

                <GridItem>
                  <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={1}>
                    Monthly Subscription Price (USD)
                  </Text>
                  <Text>
                    {onboardingData.monthly_subscription_price_usd != null
                      ? `$${onboardingData.monthly_subscription_price_usd}`
                      : "-"}
                  </Text>
                </GridItem>

                <GridItem>
                  <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={1}>
                    One Credit Price (USD)
                  </Text>
                  <Text>
                    {onboardingData.one_credit_price_usd != null
                      ? `$${onboardingData.one_credit_price_usd}`
                      : "-"}
                  </Text>
                </GridItem>

                <GridItem>
                  <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={1}>
                    Phone Number Monthly Price (USD)
                  </Text>
                  <Text>
                    {onboardingData.one_phone_number_monthly_price_usd != null
                      ? `$${onboardingData.one_phone_number_monthly_price_usd}`
                      : "-"}
                  </Text>
                </GridItem>

                <GridItem>
                  <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={1}>
                    Call Price Per Minute (USD)
                  </Text>
                  <Text>
                    {onboardingData.ai_agents_call_price_per_minute_usd != null
                      ? `$${onboardingData.ai_agents_call_price_per_minute_usd}`
                      : "-"}
                  </Text>
                </GridItem>

                <GridItem>
                  <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={1}>
                    Main Logo URL
                  </Text>
                  <Text wordBreak="break-all" fontSize="sm">
                    {onboardingData.main_logo_url || "-"}
                  </Text>
                </GridItem>

                <GridItem>
                  <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={1}>
                    Tab Logo URL
                  </Text>
                  <Text wordBreak="break-all" fontSize="sm">
                    {onboardingData.tab_logo_url || "-"}
                  </Text>
                </GridItem>
              </Grid>
            </Box>
          </Collapse>
        </Box>
      )}

      <Box
        w="100%"
        bg={bgColor}
        borderRadius="lg"
        border="1px solid"
        borderColor={borderColor}
        overflow="hidden"
      >
        <VStack spacing={4} align="stretch" p={6}>
          <HStack justify="space-between" align="center">
            <Box>
              <Heading size="lg">Users in Your Instance</Heading>
              <Text color="gray.600">
                Here are all the users in your newly created Supabase instance.
              </Text>
            </Box>
            <IconButton
              aria-label="Reload users"
              icon={<RepeatIcon />}
              onClick={handleReload}
              isLoading={loading}
              colorScheme="blue"
              variant="ghost"
              size="lg"
            />
          </HStack>

          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th>Role</Th>
                  <Th>Subscription</Th>
                  <Th>Credits</Th>
                  <Th>Created At</Th>
                </Tr>
              </Thead>
              <Tbody>
                {users.map((user) => (
                  <Tr key={user.id}>
                    <Td>
                      {user.first_name || user.last_name
                        ? `${user.first_name} ${user.last_name}`.trim()
                        : "-"}
                    </Td>
                    <Td>{user.email || "-"}</Td>
                    <Td>
                      <Badge
                        colorScheme={user.role === "admin" ? "purple" : "blue"}
                      >
                        {user.role}
                      </Badge>
                    </Td>

                    <Td>
                      <Badge
                        colorScheme={getSubscriptionColor(
                          user.subscription_status || "none"
                        )}
                      >
                        {user.subscription_status || "none"}
                      </Badge>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm">Total: {user.total_credits}</Text>
                        <Text fontSize="sm" color="gray.600">
                          Available: {user.available_credits}
                        </Text>
                      </VStack>
                    </Td>
                    <Td>
                      <Text fontSize="sm">
                        {new Date(user.created_at).toLocaleDateString()}
                      </Text>
                    </Td>
                  </Tr>
                ))}
                {users.length === 0 && (
                  <Tr>
                    <Td colSpan={7} textAlign="center" py={8}>
                      <Text color="gray.500">No users found</Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
        </VStack>
      </Box>
    </VStack>
  );
};

export default UsersTable;
